import { type AssigneeCandidateDto, SimulationStatus } from '@/api';
import { BaseModal } from '@/components/base-modal';
import Combobox, {
  type DefaultOptionType,
} from '@/components/combobox/combobox';
import { useAssigneeCandidates } from '@/pages/price-planning/simulation/simulation-queries';
import { useSimulationQueryErrorNotification } from '@/pages/price-planning/simulation/use-simulation-query-error-notification';
import { HelpText, Label } from '@lego/connect-components-react';
import { type RefObject, useState } from 'react';

type AssignToModalProps = {
  isOpen: boolean;
  simulationId: number;
  triggerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  onConfirm: (newAssignedToId: string) => void;
  isSending: boolean;
  simulationStatus: SimulationStatus;
};

const AssignToModal = ({
  isOpen,
  simulationId,
  triggerRef,
  onClose,
  onConfirm,
  isSending,
  simulationStatus,
}: AssignToModalProps) => {
  const [search, setSearch] = useState('');
  const {
    data: assignees,
    isFetching,
    isError,
    error,
  } = useAssigneeCandidates(simulationId, simulationStatus, search, isOpen);
  useSimulationQueryErrorNotification({
    isError: isOpen && isError,
    error,
    title: 'search assignee candidates',
  });

  const noEligibleUsers = !isFetching && assignees?.length === 0;

  const assigneeOptions: DefaultOptionType[] =
    assignees?.map((assignee) => ({
      id: assignee.id,
      label: assignee.name,
    })) ?? [];

  const [selectedAssignee, setSelectedAssignee] =
    useState<AssigneeCandidateDto>();
  const selected: DefaultOptionType | undefined = selectedAssignee && {
    id: selectedAssignee.id,
    label: selectedAssignee.name,
  };

  const handleAssign = () => {
    if (!selected) return;
    onConfirm(selected.id);
  };

  return (
    <BaseModal
      isOpen={isOpen}
      triggerRef={triggerRef}
      onClose={onClose}
      title="Assign to"
      size="small"
      rightButtonProps={{
        text: 'Assign',
        'aria-label': 'Assign',
        disabled: !selected || isSending,
        loading: isSending,
        onClick: handleAssign,
      }}
    >
      <div>
        <div>
          <Label>Assign to</Label>
          <div style={{ marginTop: '4px' }}>
            <Combobox
              options={assigneeOptions}
              selectedItems={selected ? [selected] : []}
              isLoading={isFetching}
              inputValue={search}
              placeholder="Type to search for a user"
              isSingleSelect
              size="medium"
              onSelect={(items) => {
                const last = [...items].pop();
                setSelectedAssignee(
                  assignees?.find((assignee) => assignee.id === last?.id),
                );
              }}
              onInputChange={(value) => {
                setSearch(value);
              }}
            />
          </div>
        </div>
        {noEligibleUsers && (
          <div style={{ marginTop: '4px' }}>
            <HelpText variant="neutral">No eligible users found.</HelpText>
          </div>
        )}
        {simulationStatus === SimulationStatus.LocalReview &&
          selectedAssignee?.isGlobal === true && (
            <div
              style={{
                marginTop: '6px',
              }}
            >
              <HelpText variant="warning">
                Assigning this to a Global reviewer will also move the status to
                Global Review.
              </HelpText>
            </div>
          )}
      </div>
    </BaseModal>
  );
};
export default AssignToModal;
